import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Icon } from "@iconify/react";
import Page from "../../Page";
import { cmsBaseUrlV1 } from "../../../react-query/endpoints";

// Coarse, charger-level status. cms `status` is "A" while the charger is
// active/online; anything else (or missing) is treated as offline.
const isOnline = (status) => {
  const s = (status || "").toUpperCase();
  return s === "A" || s === "AVAILABLE";
};

const statusChip = (status) =>
  isOnline(status)
    ? { label: "Available", color: "success" }
    : { label: "Offline", color: "default" };

const connectorSummary = (charger) => {
  const count = charger.connectorCount || 0;
  return `${count} connector${count === 1 ? "" : "s"}`;
};

const HeaderStat = ({ value, label }) => (
  <Box sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
    <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.15rem", sm: "1.3rem" }, lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.6rem",
        fontWeight: 600,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.8)",
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const SiteInformation = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [chargers, setChargers] = useState(null);
  const [siteName, setSiteName] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchSite = async () => {
      setLoading(true);
      setError(false);
      try {
        const url = `${cmsBaseUrlV1}/charger/site/${encodeURIComponent(siteId)}`;
        const response = await axios.get(url);
        const data = response?.data || {};
        if (active) {
          setChargers(Array.isArray(data.chargers) ? data.chargers : []);
          setSiteName(data.siteName || "");
          setSiteAddress(data.siteAddress || "");
        }
      } catch (e) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (siteId) fetchSite();
    return () => {
      active = false;
    };
  }, [siteId]);

  const list = chargers || [];
  const heading = siteName || `Site ${siteId}`;
  const availableCount = list.filter((c) => isOnline(c.status)).length;
  const peakKw = Math.max(0, ...list.map((c) => Number(c.maxKw) || 0));

  return (
    <Page title="Site | Charger-UI" sx={{ marginTop: { xs: "8px", md: "16px" } }}>
      <Container
        sx={{ padding: { xs: "0", sm: "0", md: "24px" }, width: "100%", mx: "auto" }}
        style={{ maxWidth: "800px" }}
      >
        <Card
          sx={{
            pb: 2,
            boxShadow: (t) =>
              t.customShadows?.z24 ||
              "0 0 4px 0 rgba(145,158,171,0.24), 0 24px 48px 0 rgba(145,158,171,0.24)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: (t) =>
                `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
              color: "#fff",
              p: { xs: 2, sm: 2.5 },
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.18)",
                  borderRadius: "12px",
                  width: 44,
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon icon="eva:pin-fill" width={24} height={24} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="inherit"
                  noWrap
                  sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem" }, lineHeight: 1.25 }}
                >
                  {heading}
                </Typography>
                {loading ? (
                  <Skeleton width={180} sx={{ bgcolor: "rgba(255,255,255,0.25)" }} />
                ) : siteAddress ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                    <Icon icon="eva:navigation-2-outline" width={13} height={13} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.85)",
                        fontSize: { xs: "0.72rem", sm: "0.8rem" },
                        lineHeight: 1.3,
                      }}
                    >
                      {siteAddress}
                    </Typography>
                  </Stack>
                ) : null}
              </Box>
            </Stack>

            {/* Stats strip */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                mt: 2,
                py: 1,
                px: 1,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.12)",
              }}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: "rgba(255,255,255,0.25)" }}
                />
              }
            >
              <HeaderStat value={loading ? "—" : list.length} label="Chargers" />
              <HeaderStat value={loading ? "—" : availableCount} label="Available" />
              <HeaderStat value={loading || !peakKw ? "—" : `${peakKw} kW`} label="Max power" />
            </Stack>
          </Box>

          {/* Body */}
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            {error && (
              <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                Couldn't load this site. Please try again.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "text.secondary" }}>
              Select a charger to continue
            </Typography>

            <List sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 0 }}>
              {loading &&
                [0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={78} sx={{ borderRadius: "14px" }} />
                ))}

              {!loading &&
                list.map((charger, i) => {
                  const chip = statusChip(charger.status);
                  return (
                    <ListItem key={charger.chargerId || i} sx={{ p: 0 }}>
                      <Paper
                        elevation={0}
                        onClick={() => navigate(`/charger/${charger.chargerId}`)}
                        sx={{
                          width: "100%",
                          cursor: "pointer",
                          borderRadius: "14px",
                          border: "1px solid",
                          borderColor: (t) => alpha(t.palette.grey[500], 0.24),
                          boxShadow: "0 2px 8px rgba(145,158,171,0.12)",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "primary.main",
                            boxShadow: (t) => `0 6px 18px ${alpha(t.palette.primary.main, 0.16)}`,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: "12px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: (t) =>
                              alpha(
                                isOnline(charger.status)
                                  ? t.palette.primary.main
                                  : t.palette.grey[500],
                                0.12
                              ),
                            color: isOnline(charger.status) ? "primary.main" : "text.disabled",
                          }}
                        >
                          <Icon icon="eva:flash-outline" width={24} height={24} />
                        </Box>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {charger.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: "block" }}
                          >
                            {charger.chargerId}
                            {"  ·  "}
                            {connectorSummary(charger)}
                          </Typography>
                        </Box>

                        <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                          <Chip
                            label={chip.label}
                            color={chip.color}
                            size="small"
                            sx={{ fontSize: "0.65rem", height: 22 }}
                          />
                          {charger.maxKw ? (
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                              {charger.maxKw} kW
                            </Typography>
                          ) : null}
                        </Stack>

                        <Box sx={{ color: "text.disabled", display: "flex", flexShrink: 0 }}>
                          <Icon icon="eva:arrow-ios-forward-fill" width={20} height={20} />
                        </Box>
                      </Paper>
                    </ListItem>
                  );
                })}

              {!loading && !error && list.length === 0 && (
                <Box sx={{ py: 6, textAlign: "center", color: "text.disabled" }}>
                  <Icon icon="eva:flash-off-outline" width={56} height={56} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    No chargers found at this site.
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
        </Card>
      </Container>
    </Page>
  );
};
